import React, { useState } from 'react';
import { Button } from 'react-bootstrap';
import type { MembershipFigures, MembershipHistoryMonth } from '../types';
import { formatCount } from '../utils/membershipFormat';
import MembershipPhoneRow, { usePhone } from './MembershipPhoneRow';
import '../membership.css';

const COLUMNS: Array<{ key: keyof MembershipFigures; label: string }> = [
  { key: 'total_members', label: 'Total' },
  { key: 'male_count', label: 'Male' },
  { key: 'female_count', label: 'Female' },
  { key: 'added_count', label: 'Added' },
  { key: 'transferred_in_count', label: 'Transferred in' },
  { key: 'deceased_count', label: 'Deceased' },
  { key: 'transferred_out_count', label: 'Transferred out' },
  { key: 'excluded_count', label: 'Excluded' },
];

const MOVEMENTS = COLUMNS.slice(3);
function valueOf(row: MembershipFigures, key: keyof MembershipFigures): number {
  const value = row[key] as number | null | undefined;
  return value ?? 0;
}

function cells(row: MembershipFigures) {
  return COLUMNS.map((column) => {
    const value = valueOf(row, column.key);
    return (
      <td key={column.key} className={`text-end${value === 0 ? ' membership-zero' : ''}`}>
        {formatCount(value)}
      </td>
    );
  });
}

function CaretLabel({ open, label }: { open: boolean; label: string }) {
  return (
    <>
      <span className="membership-caret__mark" aria-hidden="true">{open ? '\u25BE' : '\u25B8'}</span>
      <span className="membership-history__name" title={label}>{label}</span>
    </>
  );
}

function NameCell({ label, level }: { label: string; level: number }) {
  return (
    <td className={`membership-history__first membership-history__level-${level}`}>
      <span className="membership-history__spacer" aria-hidden="true" />
      <span className="membership-history__name" title={label}>{label}</span>
    </td>
  );
}

interface Props {
  scope: 'CHURCH' | 'DISTRICT' | 'NATION';
  months: MembershipHistoryMonth[];
  hasMore: boolean;
  onShowMore: () => void;
  onShowLess: () => void;
}

const MembershipHistoryTable: React.FC<Props> = ({ scope, months, hasMore, onShowMore, onShowLess }) => {
  const [openMonths, setOpenMonths] = useState<Record<string, boolean>>({});
  const [openDistricts, setOpenDistricts] = useState<Record<string, boolean>>({});
  const phone = usePhone();

  const toggle = (setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>, key: string) => {
    setter((current) => ({ ...current, [key]: !current[key] }));
  };

  if (months.length === 0) {
    return <p className="text-muted mb-0">No membership history yet</p>;
  }

  const showLess = months.length > 3;
  const footer = (hasMore || showLess) && (
    <div className="membership-history__more">
      {hasMore && (
        <Button variant="outline-secondary" size="sm" onClick={onShowMore}>
          Show 3 more months
        </Button>
      )}
      {showLess && (
        <Button variant="link" size="sm" className="membership-muted-link" onClick={onShowLess}>
          Show fewer months
        </Button>
      )}
    </div>
  );

  if (phone) {
    return (
      <div className="membership-history membership-history--cards">
        {months.map((month) => {
          const monthOpen = scope === 'CHURCH' || Boolean(openMonths[month.month_start]);
          const controls = `history-card-${month.month_start}`;
          return (
            <div className="membership-history-card" key={month.month_start}>
              <div className="membership-history-card__head">
                {scope === 'CHURCH' ? (
                  <span className="membership-history-card__month">{month.label}</span>
                ) : (
                  <button
                    type="button"
                    className="membership-caret membership-caret--row btn btn-link btn-sm p-0 text-start"
                    aria-expanded={monthOpen}
                    aria-controls={controls}
                    onClick={() => toggle(setOpenMonths, month.month_start)}
                  >
                    <CaretLabel open={monthOpen} label={month.label} />
                  </button>
                )}
                <span className="membership-history-card__total">{formatCount(valueOf(month, 'total_members'))}</span>
              </div>
              <div className="membership-history-card__sub">
                Male {formatCount(valueOf(month, 'male_count'))} {'\u00B7'} Female {formatCount(valueOf(month, 'female_count'))}
              </div>
              {monthOpen && (
                <div id={controls} className="membership-history-card__body">
                  {MOVEMENTS.map((column) => {
                    const value = valueOf(month, column.key);
                    return (
                      <div className="membership-history-card__line" key={column.key}>
                        <span>{column.label}</span>
                        <span className={value === 0 ? 'membership-zero' : undefined}>{formatCount(value)}</span>
                      </div>
                    );
                  })}
                  {scope === 'DISTRICT' && (month.churches ?? []).length > 0 && (
                    <>
                      <div className="membership-section-label mt-2">Churches</div>
                      {(month.churches ?? []).map((church) => (
                        <div key={church.church_name}><MembershipPhoneRow label={church.church_name} figures={church} /></div>
                      ))}
                    </>
                  )}
                  {scope === 'NATION' && (month.districts ?? []).length > 0 && (
                    <>
                      <div className="membership-section-label mt-2">Districts</div>
                      {(month.districts ?? []).map((district) => (
                        <div key={district.district_name}><MembershipPhoneRow label={district.district_name} figures={district}>
                          {district.churches.map((church) => (
                            <div key={church.church_name}><MembershipPhoneRow label={church.church_name} figures={church} nested /></div>
                          ))}
                        </MembershipPhoneRow></div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {footer}
      </div>
    );
  }

  const body: React.ReactNode[] = [];
  for (const month of months) {
    const monthOpen = Boolean(openMonths[month.month_start]);
    const monthControls = `history-month-${month.month_start}`;
    body.push(
      <tr key={month.month_start} className="membership-history-month">
        <th scope="row" className="membership-history__first membership-history__level-0">
          {scope === 'CHURCH' ? (
            <span className="membership-history__name" title={month.label}>{month.label}</span>
          ) : (
            <button
              type="button"
              className="membership-caret membership-caret--row btn btn-link btn-sm p-0 text-start"
              aria-expanded={monthOpen}
              aria-controls={monthControls}
              onClick={() => toggle(setOpenMonths, month.month_start)}
            >
              <CaretLabel open={monthOpen} label={month.label} />
            </button>
          )}
        </th>
        {cells(month)}
      </tr>
    );

    if (scope === 'DISTRICT' && monthOpen) {
      for (const [index, church] of (month.churches ?? []).entries()) {
        body.push(
          <tr key={`${month.month_start}-${church.church_name}`} id={index === 0 ? monthControls : undefined}>
            <NameCell label={church.church_name} level={3} />
            {cells(church)}
          </tr>
        );
      }
    }

    if (scope === 'NATION' && monthOpen) {
      const districts = month.districts ?? [];
      for (const [index, district] of districts.entries()) {
        const districtKey = `${month.month_start}-${district.district_name}`;
        const districtOpen = Boolean(openDistricts[districtKey]);
        const districtControls = `history-district-${month.month_start}-${index}`;
        body.push(
          <tr key={districtKey} id={index === 0 ? monthControls : undefined} className="membership-history-district">
            <td className="membership-history__first membership-history__level-1">
              <button
                type="button"
                className="membership-caret membership-caret--row btn btn-link btn-sm p-0 text-start"
                aria-expanded={districtOpen}
                aria-controls={districtControls}
                onClick={() => toggle(setOpenDistricts, districtKey)}
              >
                <CaretLabel open={districtOpen} label={district.district_name} />
              </button>
            </td>
            {cells(district)}
          </tr>
        );
        if (districtOpen) {
          for (const [churchIndex, church] of district.churches.entries()) {
            body.push(
              <tr
                key={`${month.month_start}-${district.district_name}-${church.church_name}`}
                id={churchIndex === 0 ? districtControls : undefined}
              >
                <NameCell label={church.church_name} level={2} />
                {cells(church)}
              </tr>
            );
          }
        }
      }
    }
  }

  return (
    <div className="membership-history">
      <div className="membership-history__frame">
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0 membership-history__table">
            <thead>
              <tr>
                <th className="membership-history__first">Month</th>
                {COLUMNS.map((column) => (
                  <th key={column.key} className="text-end">{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>{body}</tbody>
          </table>
        </div>
      </div>
      {footer}
    </div>
  );
};

export default MembershipHistoryTable;
